import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { History, ChevronRight, ExpandMore } from '@mui/icons-material';

// Strip the ULID suffix from the SK and format as "YYYY-MM-DD HH:mm:ss UTC"
const formatTimestamp = (sk) => {
  if (!sk) return '—';
  const iso = sk.split('#')[0];
  if (!iso) return sk;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, '0');
    return (
      `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
      `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
    );
  } catch {
    return iso;
  }
};

// Short summary for the ACTION column (kept compact)
const summarizeAction = (evt) => {
  const { action, section, detail = {} } = evt;
  if (section === 'DL Members') {
    const dl = detail.dl || '';
    const shortDl = dl.length > 40 ? dl.slice(0, 37) + '…' : dl;
    if (action === 'CREATE') {
      const count = detail.bulk ? (detail.members || []).length : 1;
      return `Added ${count} member${count !== 1 ? 's' : ''} to ${shortDl}`;
    }
    if (action === 'DELETE') {
      const count = detail.bulk ? (detail.members || []).length : 1;
      return `Removed ${count} member${count !== 1 ? 's' : ''} from ${shortDl}`;
    }
  }
  if (action === 'VIEW') return `Viewed ${section}`;
  if (action === 'EDIT') {
    const changed = Object.keys(detail.after || {});
    if (changed.length > 0) return `Updated ${section}: ${changed.join(', ')}`;
    return `Updated ${section}`;
  }
  return `${action} — ${section}`;
};

// ── Detail panel rendered inside the expanded row ──────────────────────────

const DetailRow = ({ label, before, after }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 130, color: 'text.secondary', fontFamily: 'monospace' }}>
      {label}
    </Typography>
    {before !== undefined ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'error.main', fontFamily: 'monospace', textDecoration: 'line-through' }}>
          {before || '—'}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>→</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'success.main', fontFamily: 'monospace' }}>
          {after || '—'}
        </Typography>
      </Box>
    ) : (
      <Typography sx={{ fontSize: '0.75rem', color: 'text.primary', fontFamily: 'monospace' }}>
        {after || '—'}
      </Typography>
    )}
  </Box>
);

const EventDetail = ({ evt }) => {
  const { action, section, detail = {} } = evt;

  // ── DL Members ────────────────────────────────────────────────────────────
  if (section === 'DL Members') {
    const members = detail.bulk
      ? (detail.members || [])
      : detail.member ? [detail.member] : [];
    const verb = action === 'CREATE' ? 'Added' : 'Removed';
    const color = action === 'CREATE' ? 'success.main' : 'error.main';
    return (
      <Box>
        <DetailRow label="Distribution List" after={detail.dl || '—'} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.25 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 130, color: 'text.secondary', fontFamily: 'monospace' }}>
            {verb}
          </Typography>
          <Box>
            {members.map((m, i) => (
              <Typography key={i} sx={{ fontSize: '0.75rem', color, fontFamily: 'monospace', lineHeight: 1.6 }}>
                {m}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── EDIT events with before/after ─────────────────────────────────────────
  if (action === 'EDIT') {
    const before = detail.before || {};
    const after = detail.after || {};
    const allKeys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

    if (allKeys.length === 0) {
      return <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>No detail available</Typography>;
    }

    // For approvers, before/after may be arrays — render as joined strings
    const fmt = (val) => {
      if (val === undefined || val === null) return '—';
      if (Array.isArray(val)) return val.length === 0 ? '—' : val.join(', ');
      return String(val);
    };

    return (
      <Box>
        {allKeys.map((key) => (
          <DetailRow
            key={key}
            label={key}
            before={fmt(before[key])}
            after={fmt(after[key])}
          />
        ))}
      </Box>
    );
  }

  return <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>No detail available</Typography>;
};

// ── Expanded detail panel (inset box below the row) ───────────────────────

const ExpandedDetail = ({ evt }) => (
  <Box sx={{
    bgcolor: 'grey.50',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    px: 2,
    py: 1.25,
    mx: 1,
    mb: 0.5,
  }}>
    <EventDetail evt={evt} />
  </Box>
);

const TYPE_COLORS = {
  EDIT: 'info',
  CREATE: 'success',
  DELETE: 'error',
  VIEW: 'default',
  ACCESS: 'default',
};

// ── Main component ─────────────────────────────────────────────────────────

const AuditLogTab = ({ account, authenticatedFetch, API_ENDPOINT }) => {
  const [events, setEvents] = useState([]);
  const [nextKey, setNextKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [showViews, setShowViews] = useState(false);

  const fetchEvents = useCallback(async (cursor = null, append = false) => {
    if (!account?.id) return;
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const url = `${API_ENDPOINT}/accounts/${account.id}/audit-log?limit=50${cursor ? `&next_key=${encodeURIComponent(cursor)}` : ''}`;
      const resp = await authenticatedFetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setEvents((prev) => append ? [...prev, ...(data.events || [])] : (data.events || []));
      setNextKey(data.next_key || null);
    } catch (e) {
      setError(e.message || 'Failed to load audit log');
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [account?.id, authenticatedFetch, API_ENDPOINT]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const toggleRow = (idx) => setExpandedRow((prev) => prev === idx ? null : idx);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Loading audit log...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>;
  }

  const visibleEvents = showViews ? events : events.filter((e) => e.action !== 'VIEW');

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <History fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>Audit Log</Typography>
          {visibleEvents.length > 0 && (
            <Chip label={visibleEvents.length} size="small" sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }} />
          )}
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showViews}
              onChange={(e) => { setShowViews(e.target.checked); setExpandedRow(null); }}
            />
          }
          label={<Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Show view events</Typography>}
          sx={{ m: 0 }}
        />
      </Box>
      <Divider sx={{ mb: 1 }} />

      {visibleEvents.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, color: 'text.disabled' }}>
          <History sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>No audit events recorded yet</Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 28, py: 0.5, px: 0.5 }} />
                  <TableCell sx={{ width: '17%', fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', py: 0.5 }}>TIME</TableCell>
                  <TableCell sx={{ width: '17%', fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', py: 0.5 }}>USER</TableCell>
                  <TableCell sx={{ width: '13%', fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', py: 0.5 }}>SECTION</TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', py: 0.5 }}>SUMMARY</TableCell>
                  <TableCell sx={{ width: '9%', fontSize: '0.78rem', fontWeight: 600, color: 'text.secondary', py: 0.5 }}>TYPE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleEvents.map((evt, i) => {
                  const isOpen = expandedRow === i;
                  return (
                    <React.Fragment key={i}>
                      <TableRow
                        hover
                        onClick={() => toggleRow(i)}
                        sx={{ cursor: 'pointer', '&:last-child td': { border: 0 }, bgcolor: isOpen ? 'action.selected' : 'inherit' }}
                      >
                        <TableCell sx={{ py: 0.5, px: 0.5, width: 28 }}>
                          <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
                            {isOpen
                              ? <ExpandMore sx={{ fontSize: 16, color: 'text.secondary' }} />
                              : <ChevronRight sx={{ fontSize: 16, color: 'text.disabled' }} />
                            }
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', py: 0.5, fontFamily: 'monospace', color: 'text.secondary' }}>
                          {formatTimestamp(evt.timestamp_ulid)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {evt.user || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', py: 0.5 }}>{evt.section || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {summarizeAction(evt)}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip
                            label={evt.action}
                            color={TYPE_COLORS[evt.action] || 'default'}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expanded detail row */}
                      <TableRow sx={{ '& td': { py: 0, border: 0 } }}>
                        <TableCell colSpan={6} sx={{ p: 0 }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <ExpandedDetail evt={evt} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {nextKey && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingMore}
                onClick={() => fetchEvents(nextKey, true)}
                sx={{ fontSize: '0.75rem' }}
              >
                {loadingMore ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                Load more
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default AuditLogTab;
